import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon, InboxArrowDownIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import {
  useExpectedDeliveries, usePoForReceiving, useReceipts, useReceipt,
  usePodPhotos,
  useCreateReceipt, useSaveReceiptLines, useConfirmReceipt, useStorerooms,
} from '../hooks/useReceiving';

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—');
const num = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 3 });


const PO_PILL = {
  sent: 'bg-sky-100 text-sky-800', approved: 'bg-sky-100 text-sky-800',
  part_received: 'bg-amber-100 text-amber-800', received: 'bg-emerald-100 text-emerald-800',
};
const GRN_PILL = { draft: 'bg-amber-100 text-amber-800', confirmed: 'bg-emerald-100 text-emerald-800', cancelled: 'bg-gray-100 text-gray-500' };
const label = (s) => String(s || '').replace('_', ' ');

// One table per storeroom (01/10/2026). An order's destination is
// stock_orders.delivery_location, which matches a storeroom name; a receipt
// has the storeroom it was booked into. Anything without one sorts last.
const NO_ROOM = 'No storeroom set';
function groupByRoom(rows, roomOf) {
  const groups = new Map();
  rows.forEach((r) => {
    const room = String(roomOf(r) || '').trim() || NO_ROOM;
    if (!groups.has(room)) groups.set(room, []);
    groups.get(room).push(r);
  });
  return [...groups.entries()].sort(([a], [b]) => (a === NO_ROOM) - (b === NO_ROOM) || a.localeCompare(b));
}
const sameRoom = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
const ROOM_KEY = 'receiving.storeroom';
const readRoom = () => { try { return localStorage.getItem(ROOM_KEY) || ''; } catch { return ''; } };
const writeRoom = (v) => { try { localStorage.setItem(ROOM_KEY, v); } catch { /* private mode */ } };

// Collapsed storerooms, remembered on this device.
const COLLAPSED_KEY = 'receiving.collapsed';
const readCollapsed = () => { try { return new Set(JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '[]')); } catch { return new Set(); } };

function RoomHeading({ room, count, noun, open, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="mb-2 flex w-full items-center gap-2 rounded-md py-1 text-left hover:bg-gray-50"
    >
      {open ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
      <h2 className="text-base font-bold text-gray-900">{room}</h2>
      <span className="text-xs text-gray-500">{count} {noun}{count === 1 ? '' : 's'}</span>
    </button>
  );
}

function Pill({ status, map }) {
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>{label(status)}</span>;
}

// A shared editable line table used by both the new-receipt and the finish-draft
// flows. `lines` carry {key, item_code, name, ordered, outstanding} + edit state.
// Counted goods, so whole numbers only -- the field used to accept 2.5 eyebolts.
// Anything typed is coerced on the way in rather than validated on the way out.
const whole = (v) => {
  const t = String(v ?? '').replace(/[^0-9]/g, '');
  return t === '' ? '' : String(parseInt(t, 10));
};

function QtyCell({ readOnly, value, onChange, tone }) {
  const ring = tone === 'amber' ? 'border-amber-300 focus:border-amber-500'
    : tone === 'red' ? 'border-red-300 focus:border-red-500' : 'border-gray-300';
  const text = tone === 'amber' ? 'text-amber-700' : tone === 'red' ? 'text-red-700' : 'text-gray-800';
  return (
    <td className="px-3 py-2 text-right">
      {readOnly ? (
        <span className={`tabular-nums font-semibold ${Number(value) ? text : 'text-gray-300'}`}>{Number(value) || 0}</span>
      ) : (
        <input
          type="text" inputMode="numeric" value={value ?? ''}
          onChange={(ev) => onChange(whole(ev.target.value))}
          placeholder="0"
          className={`w-20 rounded-md border px-2 py-1 text-right text-sm tabular-nums ${ring}`}
        />
      )}
    </td>
  );
}

function LineTable({ rows, edits, setEdit, readOnly, rowAction }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">Item</th>
            <th className="px-3 py-2 text-right">Ordered</th>
            <th className="px-3 py-2 text-right">Outstanding</th>
            <th className="px-3 py-2 text-right">Received</th>
            <th className="px-3 py-2 text-right">Damaged</th>
            <th className="px-3 py-2 text-right">Rejected</th>
            <th className="px-3 py-2">Note</th>
            {rowAction ? <th className="px-3 py-2" /> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => {
            const e = edits[r.key] || {};
            return (
              <tr key={r.key}>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-800">{r.name}</div>
                  <div className="font-mono text-xs text-gray-500">{r.item_code}</div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{num(r.ordered)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">{num(r.outstanding)}</td>
                {/* Three separate counts rather than one quantity and a
                    condition: a single delivery line can be part good, part
                    smashed and part the wrong item all at once, and the old
                    shape forced the storeman to pick one story for the lot. */}
                <QtyCell readOnly={readOnly} value={e.qty} onChange={(v) => setEdit(r.key, { qty: v })} />
                <QtyCell readOnly={readOnly} value={e.damaged} tone="amber" onChange={(v) => setEdit(r.key, { damaged: v })} />
                <QtyCell readOnly={readOnly} value={e.rejected} tone="red" onChange={(v) => setEdit(r.key, { rejected: v })} />
                <td className="px-3 py-2">
                  {readOnly ? <span className="text-gray-500">{e.note || '—'}</span> : (
                    <input type="text" value={e.note || ''} onChange={(ev) => setEdit(r.key, { note: ev.target.value })} placeholder="Discrepancy…" className="w-40 rounded-md border border-gray-300 px-2 py-1 text-sm" />
                  )}
                </td>
                {rowAction ? <td className="px-3 py-2 text-right">{rowAction(r)}</td> : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Drawer({ title, subtitle, children, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-lg bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div><h2 className="text-lg font-bold text-gray-900">{title}</h2>{subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}</div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// New delivery against a PO — creates + confirms in one go (no lingering draft),
// or "Save as draft" to finish later.
// "No longer needed" (07/10/2026): writes off what is still owed on a line so
// it stops counting as outstanding; Undo puts it back.
function useLineNeeded() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, restore, reason }) =>
      (await api.post(`/api/receiving/po-lines/${id}/${restore ? 'restore' : 'not-needed'}`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['receiving'] }),
  });
}

function NotNeededButton({ line, onDone }) {
  const mark = useLineNeeded();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  if (!asking) {
    return (
      <button type="button" onClick={() => setAsking(true)} className="whitespace-nowrap text-xs font-medium text-gray-500 hover:text-rose-700 hover:underline">
        No longer needed
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="w-36 rounded-md border border-gray-300 px-2 py-1 text-xs" />
      <button
        type="button"
        disabled={mark.isPending}
        onClick={async () => {
          try { await mark.mutateAsync({ id: line.poLineId, reason }); onDone(`${line.name}: ${num(line.outstanding)} no longer needed.`); } catch (e) { onDone(e?.response?.data?.error || 'Could not update the line.', true); }
        }}
        className="rounded-md bg-rose-600 px-2 py-1 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
      >
        Confirm
      </button>
      <button type="button" onClick={() => setAsking(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
    </div>
  );
}

function ReceiveDrawer({ po, onClose }) {
  const lineNeeded = useLineNeeded();
  const { data, isLoading } = usePoForReceiving(po.id);
  const { data: storerooms } = useStorerooms();
  const create = useCreateReceipt();
  const saveLines = useSaveReceiptLines();
  const confirm = useConfirmReceipt();

  const [storeroomId, setStoreroomId] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [note, setNote] = useState('');
  const [edits, setEdits] = useState({});
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);

  // Pre-select the storeroom the order was raised for (matched on name), so
  // the person booking it in only has to change it if it went elsewhere.
  useEffect(() => {
    if (storeroomId || !storerooms?.length) return;
    const want = data?.delivery_location || po.delivery_location;
    const match = storerooms.find((r) => sameRoom(r.name, want));
    if (match) setStoreroomId(String(match.id));
  }, [storerooms, data, po, storeroomId]);

  const rows = useMemo(() => (data?.lines || []).filter((l) => Number(l.outstanding) > 0)
    .map((l) => ({ key: l.id, poLineId: l.id, item_code: l.item_code, name: l.name, ordered: l.quantity, outstanding: l.outstanding })), [data]);

  useEffect(() => {
    const init = {};
    rows.forEach((r) => { init[r.key] = { qty: String(r.outstanding), condition: 'ok', note: '' }; });
    setEdits(init);
  }, [rows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const setEdit = (key, patch) => setEdits((s) => ({ ...s, [key]: { ...s[key], ...patch } }));
  const cancelledLines = (data?.lines || []).filter((l) => Number(l.qty_cancelled) > 0);

  const buildLines = (draftLines) => draftLines.map((dl) => {
    const e = edits[dl.purchase_order_line_id] || {};
    return {
      id: dl.id,
      qty_received: Number(e.qty || 0),
      qty_damaged: Number(e.damaged || 0),
      qty_rejected: Number(e.rejected || 0),
      reject_reason: e.rejected ? (e.note || '') : '',
      // Kept for the existing receipt view; the three counts are the record now.
      // 'rejected' (wrong stock) is its own value now, not folded into
      // 'damaged' -- they are two different conversations with the supplier.
      // Same precedence the FSM screen uses, so a line booked in from either
      // place lands on the same value.
      line_condition: Number(e.rejected) ? 'rejected' : Number(e.damaged) ? 'damaged' : (e.condition || 'ok'),
      discrepancy_note: e.note || '',
    };
  });

  const run = async (confirmIt) => {
    setFlash(null);
    if (confirmIt && !storeroomId) { setFlash({ ok: false, msg: 'Choose the storeroom the goods were received into.' }); return; }
    const anyQty = Object.values(edits).some((e) => Number(e.qty || 0) > 0);
    if (!anyQty) { setFlash({ ok: false, msg: 'Enter a received quantity on at least one line.' }); return; }
    setBusy(true);
    try {
      const draft = await create.mutateAsync({ po_id: po.id, storeroom_id: storeroomId || null, delivery_note_ref: deliveryNote || null, note: note || null });
      await saveLines.mutateAsync({ id: draft.id, lines: buildLines(draft.lines) });
      if (confirmIt) {
        const r = await confirm.mutateAsync({ id: draft.id, storeroom_id: storeroomId, note: note || null });
        setFlash({ ok: true, msg: `${r.grn_number} received — PO is now ${label(r.po_status)}.` });
        setTimeout(onClose, 900);
      } else {
        setFlash({ ok: true, msg: `Saved as draft ${draft.grn_number}. Finish it from Receipt history.` });
        setTimeout(onClose, 900);
      }
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.error || 'Could not save the receipt.' });
    } finally { setBusy(false); }
  };

  return (
    <Drawer title={`Receive ${po.po_number}`} subtitle={`${po.supplier_name}${po.xero_po_number ? ` · Xero ${po.xero_po_number}` : ''}${Number(po.no_approval) === 1 ? ` · No approval - call-off ${po.call_off_ref}` : ''}`} onClose={onClose}>
      {isLoading ? <div className="text-sm text-gray-400">Loading…</div> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-500">Received into storeroom *</span>
              <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="">— choose —</option>
                {(storerooms || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-500">Delivery note ref</span>
              <input type="text" value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
            </label>
          </div>

          {rows.length === 0 ? <p className="text-sm text-gray-500">Nothing left to receive on this PO.</p> : (
            <LineTable
              rows={rows}
              edits={edits}
              setEdit={setEdit}
              rowAction={(r) => <NotNeededButton line={r} onDone={(msg, bad) => setFlash({ ok: !bad, msg })} />}
            />
          )}

          {cancelledLines.length ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="mb-1 text-xs font-semibold text-gray-500">No longer needed</p>
              <ul className="space-y-1">
                {cancelledLines.map((l) => (
                  <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="text-gray-600">
                      <span className="text-gray-800">{l.name}</span> · {num(l.qty_cancelled)} written off
                      {l.cancelled_by_name ? ` by ${l.cancelled_by_name}` : ''}{l.cancelled_at ? ` on ${fmtDate(l.cancelled_at)}` : ''}
                      {l.cancel_reason ? <span className="text-gray-400"> · {l.cancel_reason}</span> : null}
                    </span>
                    <button
                      type="button"
                      disabled={lineNeeded.isPending}
                      onClick={async () => {
                        try { await lineNeeded.mutateAsync({ id: l.id, restore: true }); setFlash({ ok: true, msg: `${l.name} is expected again.` }); } catch (e) { setFlash({ ok: false, msg: e?.response?.data?.error || 'Could not undo.' }); }
                      }}
                      className="text-xs font-medium text-brand hover:text-brand-dark hover:underline"
                    >
                      Undo
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-500">Note (optional)</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          </label>

          {flash && <div className={`rounded-lg px-4 py-2.5 text-sm font-medium ${flash.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`}>{flash.msg}</div>}

          <div className="flex items-center gap-2">
            <button type="button" disabled={busy || !rows.length} onClick={() => run(true)} className="flex-1 rounded-md bg-brand py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
              {busy ? 'Saving…' : 'Confirm receipt'}
            </button>
            <button type="button" disabled={busy || !rows.length} onClick={() => run(false)} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">Save as draft</button>
          </div>
        </div>
      )}
    </Drawer>
  );
}

// View a receipt; a draft can be finished (counts + confirm) from here.
// The delivery notes the storeman photographed, for whoever is arguing a short
// or damaged delivery with a supplier. Download matters as much as viewing:
// the note usually has to be attached to an email or a credit-note claim.
function PodPhotos({ grnId, grnNumber }) {
  const { data, isLoading, isError } = usePodPhotos(grnId);
  const photos = data?.photos || (data?.photo ? [{ id: 0, photo: data.photo }] : []);

  // Data URLs already hold the bytes, so a download needs no second request.
  const filename = (i, src) => {
    const ext = /^data:image\/png/.test(src) ? 'png' : 'jpg';
    return `${grnNumber || 'delivery-note'}-${i + 1}.${ext}`;
  };

  if (isLoading) return <p className="mt-4 text-sm text-gray-400">Loading delivery notes…</p>;
  if (isError || !photos.length) {
    return (
      <p className="mt-4 rounded-md border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-400">
        No delivery note was photographed for this receipt.
      </p>
    );
  }
  return (
    <div className="mt-5">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">
          Delivery notes ({photos.length})
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {photos.map((ph, i) => (
          <div key={ph.id != null ? ph.id : i} className="rounded-lg border border-gray-200 p-2">
            {/* Opens full size in a new tab; the thumbnail is unreadable for a
                handwritten note. */}
            <a href={ph.photo} target="_blank" rel="noreferrer">
              <img src={ph.photo} alt={`Delivery note ${i + 1} for ${grnNumber}`}
                className="h-40 w-full rounded object-cover" />
            </a>
            <a href={ph.photo} download={filename(i, ph.photo)}
              className="mt-2 block text-center text-xs font-semibold text-brand hover:underline">
              Download
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReceiptDrawer({ grnId, onClose }) {
  const { data, isLoading } = useReceipt(grnId);
  const { data: storerooms } = useStorerooms();
  const saveLines = useSaveReceiptLines();
  const confirm = useConfirmReceipt();
  const [storeroomId, setStoreroomId] = useState('');
  const [edits, setEdits] = useState({});
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);
  const isDraft = data && data.status === 'draft';

  const rows = useMemo(() => (data?.lines || []).map((l) => ({
    key: l.id, item_code: l.item_code, name: l.name, ordered: l.qty_ordered, outstanding: l.qty_ordered,
  })), [data]);

  useEffect(() => {
    if (!data) return;
    setStoreroomId(data.storeroom_id ? String(data.storeroom_id) : '');
    const init = {};
    (data.lines || []).forEach((l) => {
      init[l.id] = {
        qty: String(Number(l.qty_received) || 0),
        damaged: String(Number(l.qty_damaged) || 0),
        rejected: String(Number(l.qty_rejected) || 0),
        condition: l.line_condition || 'ok',
        note: l.discrepancy_note || l.reject_reason || '',
      };
    });
    setEdits(init);
  }, [data]);

  const setEdit = (key, patch) => setEdits((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const doConfirm = async () => {
    setFlash(null);
    if (!storeroomId) { setFlash({ ok: false, msg: 'Choose the storeroom.' }); return; }
    setBusy(true);
    try {
      await saveLines.mutateAsync({ id: grnId, lines: (data.lines || []).map((l) => ({
        id: l.id,
        qty_received: Number(edits[l.id]?.qty || 0),
        qty_damaged: Number(edits[l.id]?.damaged || 0),
        qty_rejected: Number(edits[l.id]?.rejected || 0),
        reject_reason: edits[l.id]?.rejected ? (edits[l.id]?.note || '') : '',
        line_condition: edits[l.id]?.condition || 'ok',
        discrepancy_note: edits[l.id]?.note || '',
      })) });
      const r = await confirm.mutateAsync({ id: grnId, storeroom_id: storeroomId });
      setFlash({ ok: true, msg: `${r.grn_number} received — PO is now ${label(r.po_status)}.` });
      setTimeout(onClose, 900);
    } catch (e) { setFlash({ ok: false, msg: e?.response?.data?.error || 'Could not confirm.' }); }
    finally { setBusy(false); }
  };

  if (isLoading || !data) return <Drawer title="Receipt" onClose={onClose}><div className="text-sm text-gray-400">Loading…</div></Drawer>;

  return (
    <Drawer title={data.grn_number} subtitle={`${data.po_number} · ${data.supplier_name}`} onClose={onClose}>
      <div className="mb-3 flex items-center gap-3">
        <Pill status={data.status} map={GRN_PILL} />
        {data.received_by_name && <span className="text-xs text-gray-500">by {data.received_by_name}</span>}
        {data.confirmed_at && <span className="text-xs text-gray-500">on {fmtDate(data.confirmed_at)}</span>}
        {data.delivery_note_ref && <span className="text-xs text-gray-500">DN {data.delivery_note_ref}</span>}
      </div>

      {isDraft ? (
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-semibold text-gray-500">Received into storeroom *</span>
          <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)} className="w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm">
            <option value="">— choose —</option>
            {(storerooms || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      ) : (
        <p className="mb-3 text-sm text-gray-600">Received into <span className="font-medium">{data.storeroom_name || '—'}</span></p>
      )}

      <LineTable rows={rows} edits={edits} setEdit={setEdit} readOnly={!isDraft} />

      <PodPhotos grnId={grnId} grnNumber={data.grn_number} />

      {flash && <div className={`mt-4 rounded-lg px-4 py-2.5 text-sm font-medium ${flash.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`}>{flash.msg}</div>}

      {isDraft && (
        <button type="button" disabled={busy} onClick={doConfirm} className="mt-4 w-full rounded-md bg-brand py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
          {busy ? 'Confirming…' : 'Confirm receipt'}
        </button>
      )}
    </Drawer>
  );
}

function ExpectedTab({ onReceive, room, collapsed, onToggle }) {
  const { data, isLoading, isError } = useExpectedDeliveries();
  const orders = (data || []).filter((o) => !room || sameRoom(o.delivery_location, room));
  if (isLoading) return <p className="px-4 py-6 text-center text-sm text-gray-400">Loading…</p>;
  if (isError) return <p className="px-4 py-6 text-center text-sm text-rose-500">Could not load expected deliveries.</p>;
  if (!orders.length) {
    return (
      <p className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-400">
        {room ? `Nothing waiting to be received at ${room}.` : 'No open purchase orders — nothing to receive right now.'}
      </p>
    );
  }
  return (
    <div>
      {groupByRoom(orders, (o) => o.delivery_location).map(([roomName, list]) => (
        <div key={roomName} className="mt-6 first:mt-0">
          <RoomHeading room={roomName} count={list.length} noun="order" open={Boolean(room) || !collapsed.has(roomName)} onToggle={() => onToggle(roomName)} />
          {(Boolean(room) || !collapsed.has(roomName)) && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2.5">PO</th><th className="px-4 py-2.5">Supplier</th>
                  <th className="px-4 py-2.5 text-right">Ordered</th><th className="px-4 py-2.5 text-right">Received</th><th className="px-4 py-2.5 text-right">Outstanding</th>
                  <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Raised</th><th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.map((o) => (
                  <tr key={o.id} className="hover:bg-brand-light/40">
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <div className="font-medium text-gray-800">{o.po_number}</div>
                      {o.xero_po_number && <div className="text-xs text-gray-400">Xero {o.xero_po_number}</div>}
                      {Number(o.no_approval) === 1 && <div className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800">No approval - call-off {o.call_off_ref}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-700">{o.supplier_name}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{num(o.qty_ordered)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{num(o.qty_received)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-800">{num(o.qty_outstanding)}</td>
                    <td className="px-4 py-2.5"><Pill status={o.status} map={PO_PILL} /></td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-500">{fmtDate(o.created_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button type="button" onClick={() => onReceive(o)} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90">
                        <InboxArrowDownIcon className="h-4 w-4" /> Receive
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Every delivery against one order, in one place (07/10/2026). An order that
// arrives in two drops used to be two GRNs to open one after the other; this
// lays them side by side per item — what was ordered, what came on which day,
// and what is still to come — with each GRN still openable for its photos.
function OrderDeliveries({ order, onOpenGrn, onClose }) {
  const { data: po, isLoading } = usePoForReceiving(order.poId);
  const deliveries = [...order.receipts].sort(
    (a, b) => new Date(a.confirmed_at || a.created_at) - new Date(b.confirmed_at || b.created_at)
  );
  const cell = (r, lineId) => (r.lines || []).filter((l) => l.purchase_order_line_id === lineId);
  const sum = (ls, k) => ls.reduce((t, l) => t + Number(l[k] || 0), 0);
  const subtitle = [order.supplier, order.room, `${deliveries.length} deliver${deliveries.length === 1 ? 'y' : 'ies'}`]
    .filter(Boolean).join(' · ');

  return (
    <Drawer title={`${order.poNumber}${po?.order_reference ? ` · ${po.order_reference}` : ''}`} subtitle={subtitle} onClose={onClose}>
      {isLoading ? <p className="py-6 text-center text-sm text-gray-400">Loading…</p> : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2 text-right">Ordered</th>
                {deliveries.map((r) => (
                  <th key={r.id} className="whitespace-nowrap px-3 py-2 text-right normal-case">
                    <span className="block text-gray-700">{fmtDate(r.confirmed_at || r.created_at)}</span>
                    <span className="block font-normal text-gray-400">{r.grn_number}{r.status === 'draft' ? ' (draft)' : ''}</span>
                  </th>
                ))}
                <th className="px-3 py-2 text-right">Received</th>
                <th className="px-3 py-2 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(po?.lines || []).map((l) => {
                const counted = deliveries.filter((r) => r.status === 'confirmed').flatMap((r) => cell(r, l.id));
                const received = sum(counted, 'qty_received');
                return (
                  <tr key={l.id}>
                    <td className="px-3 py-2">
                      <span className="block text-gray-800">{l.name}</span>
                      <span className="block text-xs text-gray-400">{l.item_code}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">{num(l.quantity)}</td>
                    {deliveries.map((r) => {
                      const ls = cell(r, l.id);
                      const got = sum(ls, 'qty_received');
                      const dmg = sum(ls, 'qty_damaged');
                      const rej = sum(ls, 'qty_rejected');
                      const subs = ls.filter((x) => x.received_item_code).map((x) => x.received_item_code);
                      return (
                        <td key={r.id} className={`px-3 py-2 text-right tabular-nums ${r.status === 'draft' ? 'text-gray-400' : 'text-gray-700'}`}>
                          {got ? num(got) : (dmg || rej ? 0 : '—')}
                          {dmg ? <span className="block text-xs text-amber-700">{num(dmg)} damaged</span> : null}
                          {rej ? <span className="block text-xs text-rose-700">{num(rej)} rejected</span> : null}
                          {subs.length ? <span className="block text-xs text-gray-400">as {subs.join(', ')}</span> : null}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">{num(received)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${Number(l.outstanding) > 0 ? 'font-semibold text-amber-700' : 'text-gray-400'}`}>
                      {Number(l.outstanding) > 0 ? num(l.outstanding) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="mb-2 mt-5 text-sm font-semibold text-gray-900">Deliveries</h3>
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {deliveries.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
            <span className="text-gray-700">
              <span className="font-medium text-gray-900">{r.grn_number}</span>
              {' · '}{fmtDate(r.confirmed_at || r.created_at)}
              {r.received_by_name ? ` · ${r.received_by_name}` : ''}
              {r.delivery_note_ref ? ` · note ${r.delivery_note_ref}` : ''}
              {Number(r.photo_count) ? ` · ${r.photo_count} photo${Number(r.photo_count) === 1 ? '' : 's'}` : ''}
            </span>
            <span className="flex items-center gap-2">
              <Pill status={r.status} map={GRN_PILL} />
              <button type="button" onClick={() => onOpenGrn(r.id)} className="text-xs font-medium text-brand hover:text-brand-dark hover:underline">
                Open GRN
              </button>
            </span>
          </li>
        ))}
      </ul>
    </Drawer>
  );
}

// One row per ORDER (purchase order), however many deliveries it came in.
function groupByOrder(receipts) {
  const m = new Map();
  for (const r of receipts) {
    if (!m.has(r.purchase_order_id)) {
      m.set(r.purchase_order_id, {
        poId: r.purchase_order_id, poNumber: r.po_number, supplier: r.supplier_name, room: r.storeroom_name, receipts: [],
      });
    }
    m.get(r.purchase_order_id).receipts.push(r);
  }
  return [...m.values()].map((o) => ({
    ...o,
    qty: o.receipts.reduce((t, r) => t + (r.status === 'confirmed' ? Number(r.qty_total || 0) : 0), 0),
    photos: o.receipts.reduce((t, r) => t + Number(r.photo_count || 0), 0),
    hasDraft: o.receipts.some((r) => r.status === 'draft'),
    last: o.receipts.reduce((d, r) => {
      const x = new Date(r.confirmed_at || r.created_at);
      return x > d ? x : d;
    }, new Date(0)),
  })).sort((a, b) => b.last - a.last);
}

function HistoryTab({ onOpen, room, collapsed, onToggle }) {
  const { data, isLoading } = useReceipts();
  const rows = (data || []).filter((r) => !room || sameRoom(r.storeroom_name, room));
  if (isLoading) return <p className="px-4 py-6 text-center text-sm text-gray-400">Loading…</p>;
  if (!rows.length) {
    return (
      <p className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-400">
        {room ? `No goods receipts at ${room} yet.` : 'No goods receipts yet.'}
      </p>
    );
  }
  return (
    <div>
      {groupByRoom(rows, (r) => r.storeroom_name).map(([roomName, list]) => (
        <div key={roomName} className="mt-6 first:mt-0">
          <RoomHeading room={roomName} count={groupByOrder(list).length} noun="order" open={Boolean(room) || !collapsed.has(roomName)} onToggle={() => onToggle(roomName)} />
          {(Boolean(room) || !collapsed.has(roomName)) && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2.5">PO</th><th className="px-4 py-2.5">Deliveries</th><th className="px-4 py-2.5">Supplier</th>
                  <th className="px-4 py-2.5 text-right">Qty</th><th className="px-4 py-2.5 text-right">Notes</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Last delivery</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groupByOrder(list).map((o) => (
                  <tr key={o.poId} onClick={() => onOpen(o)} className="cursor-pointer hover:bg-brand-light/40">
                    <td className="whitespace-nowrap px-4 py-2.5 font-medium text-gray-800">{o.poNumber}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {o.receipts.length}
                      <span className="ml-1 text-xs text-gray-400">({o.receipts.map((r) => r.grn_number).join(', ')})</span>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-700">{o.supplier}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{num(o.qty)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{o.photos || '—'}</td>
                    <td className="px-4 py-2.5"><Pill status={o.hasDraft ? 'draft' : 'confirmed'} map={GRN_PILL} /></td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-500">{fmtDate(o.last)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Receiving() {
  const [tab, setTab] = useState('expected');
  // Remembered on this device: a storeman nearly always works one storeroom.
  const [room, setRoomState] = useState(readRoom);
  const setRoom = (v) => { setRoomState(v); writeRoom(v); };
  const { data: storerooms } = useStorerooms();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleRoom = (name) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next])); } catch { /* private mode */ }
    return next;
  });
  const [receivePo, setReceivePo] = useState(null);
  const [openGrn, setOpenGrn] = useState(null);
  const [openOrder, setOpenOrder] = useState(null);

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Goods Receiving</h1>
        <p className="mt-1 text-sm text-gray-500">Book in deliveries against your purchase orders and bring the stock into inventory.</p>
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-2 border-b border-gray-200">
        <div className="flex gap-1">
          {[['expected', 'Expected deliveries'], ['history', 'Receipt history']].map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === k ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {lbl}
            </button>
          ))}
        </div>
        <select
          value={room}
          onChange={(e) => setRoom(e.target.value)}
          aria-label="Storeroom"
          className="mb-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
        >
          <option value="">All storerooms</option>
          {(storerooms || []).map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
        </select>
      </div>

      {tab === 'expected' ? <ExpectedTab onReceive={setReceivePo} room={room} collapsed={collapsed} onToggle={toggleRoom} /> : <HistoryTab onOpen={setOpenOrder} room={room} collapsed={collapsed} onToggle={toggleRoom} />}

      {receivePo && <ReceiveDrawer po={receivePo} onClose={() => setReceivePo(null)} />}
      {openOrder && <OrderDeliveries order={openOrder} onOpenGrn={setOpenGrn} onClose={() => setOpenOrder(null)} />}
      {openGrn && <ReceiptDrawer grnId={openGrn} onClose={() => setOpenGrn(null)} />}
    </div>
  );
}

export default Receiving;
